import type { Scope } from '@sentry/browser'
import type { LoggerAdapter } from '../types'
import { gatherErrorCauseChain } from '../../gatherErrorCauseChain'

const IGNORED_ERRORS = [
  'Network Error',
  'Unable to fetch result (Network Error)',
  /^Failed to fetch \(.+\)$/, // https://wix-o.sentry.io/issues/6941193207/
  'Failed to fetch', // https://wix-o.sentry.io/issues/7098500617/
  /^NetworkError when attempting to fetch resource. \(.+\)$/, // https://wix-o.sentry.io/issues/7119035454/
  /^Load failed \(.+\)$/, // https://wix-o.sentry.io/issues/7119239206/
  'Request aborted',
  'Unable to fetch result (Request aborted)',
  'timeout of 0ms exceeded',
  'Unable to fetch result (timeout of 0ms exceeded)',
  'Request failed with status code 401',
  'Unable to fetch result (Request failed with status code 401)',
  'Request failed with status code 403',
  'Unable to fetch result (Request failed with status code 403)',
  'Request failed with status code 499',
  'Unable to fetch result (Request failed with status code 499)',
  'Request failed with status code 500',
  'Unable to fetch result (Request failed with status code 500)',
  'Request failed with status code 501',
  'Unable to fetch result (Request failed with status code 501)',
  'Request failed with status code 502',
  'Unable to fetch result (Request failed with status code 502)',
  'Request failed with status code 503',
  'Unable to fetch result (Request failed with status code 503)',
  'Request failed with status code 504',
  'Unable to fetch result (Request failed with status code 504)',
  // stopping ignore to collect fresh data: 'Failed to list all controllers', // ignoring until https://wix.atlassian.net/browse/DM-14002 is resolved
  'Wix Code is installed, but Wix Code public API is not available', // ignoring until this issue is resolved https://wix.slack.com/archives/C025ST7GB3N/p1766145215148469
  'Unexpected token \'x\', "x"xxxxxxxx"... is not valid JSON', // ignoring until this issue is resolved https://wix.slack.com/archives/CQQ6MNTV5/p1766318763504529
]

const shouldIgnoreError = (errorCauseChain: any[]) => {
  return IGNORED_ERRORS.some(matcher =>
    errorCauseChain.some(error => {
      if (typeof error !== 'object' || error === null) {
        return false
      }

      const { message } = error
      if (typeof matcher === 'string') {
        return matcher === message
      } else if (matcher instanceof RegExp) {
        return !!message && matcher.test(message)
      } else {
        return false
      }
    }),
  )
}

export type SentryAdapter = Required<
  Pick<LoggerAdapter, 'error' | 'breadcrumb' | 'updateContext'>
>

const SLED_USER_ID = '1b2a4fbf-71cd-4e78-9536-f9c31dee224b'

export const sentryAdapter = (client: Scope): SentryAdapter => {
  const shouldLog = () => client.getUser()?.id !== SLED_USER_ID

  const adapter: SentryAdapter = {
    error: (error, extras) => {
      try {
        if (!shouldLog()) {
          return
        }
        const errorCauseChain = gatherErrorCauseChain(error)

        if (shouldIgnoreError(errorCauseChain)) {
          client.addBreadcrumb({
            message: 'Ignored an error',
            category: 'sentryAdapter',
            data: { errorCauseChain },
          })
          return
        }

        const scope = client.clone()
        scope.setTag('priority', 'P3')
        scope.setExtras({
          ...(extras || {}),
          errorCauseChain,
        })
        scope.captureException(error)
      } catch (loggerError) {
        client.captureException(
          new Error('sentryAdapter.error failed', { cause: loggerError }),
        )
      }
    },
    breadcrumb: ({ message, category, data }) => {
      if (!shouldLog()) {
        return
      }
      client.addBreadcrumb({
        message,
        category,
        data,
      })
    },
    updateContext: context => {
      if ('tags' in context && context.tags) {
        client.setTags(context.tags)
      }
      if ('userId' in context && context.userId) {
        client.setUser({ id: context.userId })
      }
      if ('experiments' in context && context.experiments) {
        client.setExtras({ experiments: context.experiments })
      }
    },
  }

  return adapter
}
